import { node, starIcon } from "~/util/dom";
import { IStarsInterval } from "./interfaces";
import { _ } from "~/i18n";

interface IFirmReviewStarsPicker {
	starWidth?: number;
	starOffset?: number;
	starCount?: number;
	noTitle?: boolean;
	clickCb?: (starsItem: IStarsInterval, oldStarsItem: IStarsInterval) => void;
}

// ui pro vyber hvezdicek
export default class FirmReviewStarsPicker {
	_opts: IFirmReviewStarsPicker;

	_dom: {
		container: HTMLElement;
		stars?: HTMLElement;
		starsInner?: HTMLElement;
		starsTitle?: HTMLElement;
		starsArray?: Array<HTMLElement>;
	};

	_enabled: boolean;

	_notSet: boolean;

	_starsIntervals: Array<IStarsInterval>;

	_starsItem: IStarsInterval;

	_savedStarsItem: null | IStarsInterval;

	_active: boolean;

	constructor(optsArg: IFirmReviewStarsPicker) {
		this._opts = {
			starWidth: 30,
			starOffset: 10,
			starCount: 5,
			noTitle: false,
			clickCb: () => { /**/ }, // cb pri kliknuti na hvezdy
			...optsArg,
		};

		this._dom = {
			container: node("div", { className: "firm-review-stars-picker" }),
		};

		this._enabled = true;
		this._notSet = true;
		this._starsIntervals = this._getStarsIntervals();
		this._starsItem = this._getStarsItem();
		this._savedStarsItem = null;
		this._active = true;

		// vytvorime
		this._build();
	}

	destroy() {
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get starsItem() {
		return this._starsItem;
	}

	set starsItem(starsItem) {
		this._notSet = false;
		this._starsItem = starsItem;
		this._colorStars(this._starsItem);
	}

	set percent(percent: number) {
		this.starsItem = this._getStarsItem(percent);
	}

	set stars(starsCount: number) {
		const percent = Math.floor(starsCount / this._opts.starCount * 100);

		this.starsItem = this._getStarsItem(percent);
	}

	/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
	get percent() {
		return this._starsItem.percent;
	}

	get realPercent() {
		return this._notSet ? 0 : this._starsItem.percent;
	}

	set title(title: string) {
		if (this._dom.starsTitle) {
			this._dom.starsTitle.innerHTML = title || "";
		}
	}

	set enabled(state: boolean) {
		this._enabled = state;
	}

	// aktivni - zlate hvezdy, neaktivni zase sede
	set active(active: boolean) {
		this._active = active;
		this._dom.starsArray.forEach(elem => {
			elem.classList[this._active ? "remove" : "add"]("gray-bg");
		});
	}

	setDefaultTitle() {
		if (this._dom.starsTitle) {
			this._dom.starsTitle.textContent = _("detail.content.review2.titleExperience");
		}
	}

	setVisitTitle() {
		if (this._dom.starsTitle) {
			this._dom.starsTitle.textContent = _("detail.content.review2.titleVisit");
		}
	}

	save(starsItem) {
		this._savedStarsItem = starsItem || this._starsItem;
	}

	load() {
		if (this._savedStarsItem) {
			this.starsItem = this._savedStarsItem;
			this._savedStarsItem = null;
		}
	}

	_build() {
		const stars = node("div", { className: "stars" });
		const starsInner = node("div", { className: "stars-inner" });

		starsInner.addEventListener("click", event => {
			this._starsClick(event);
		});
		starsInner.addEventListener("pointermove", event => {
			this._starsMove(event);
		});
		starsInner.addEventListener("pointerleave", () => {
			this._starsLeave();
		});
		const starsArray = [];

		for (let ind = 0; ind < this._opts.starCount; ind++) {
			const elem = starIcon();

			elem.classList.add("white-bg");
			elem.style.width = `${this._opts.starWidth}px`;
			elem.style.height = `${this._opts.starWidth}px`;

			if (ind > 0) {
				elem.style.marginLeft = `${this._opts.starOffset}px`;
			}

			starsInner.appendChild(elem);
			starsArray.push(elem);
		}

		stars.appendChild(starsInner);
		this._dom.container.insertBefore(stars, this._dom.container.firstChild);


		if (this._dom.starsTitle) {
			this._dom.starsTitle.remove();
		}

		if (!this._opts.noTitle) {
			const starsTitle = node("h3", { className: "title" });

			this._dom.container.insertBefore(starsTitle, this._dom.container.firstChild);
			this._dom.starsTitle = starsTitle;
		}

		if (this._dom.stars) {
			this._dom.stars.remove();
		}

		Object.assign(this._dom, { starsArray, stars, starsInner });

		this.setDefaultTitle();
	}

	_starsClick(event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();

		if (!this._enabled) {
			return;
		}

		// ulozime
		const oldStarsItem = this._starsItem;

		this._starsItem = this._getItemFromEvent(event);
		// obarvime
		this._colorStars(this._starsItem, true);
		// callback funkce - klik
		this._opts.clickCb && this._opts.clickCb(this._starsItem, oldStarsItem);
	}

	_starsMove(event: MouseEvent) {
		if (!this._enabled) {
			return;
		}

		// update hvezd
		this._colorStars(this._getItemFromEvent(event), true);
	}

	_starsLeave() {
		if (!this._enabled) {
			return;
		}

		// reset
		this._colorStars(this._starsItem, !this._notSet);
	}

	_colorStars(starItem: IStarsInterval, oneStarMin?: boolean) {
		if (!starItem) {
			return;
		}

		this._dom.starsArray.forEach((item, ind) => {
			item.classList.remove("half");
			item.classList.remove("full");

			if (ind <= starItem.stars - 1) {
				if (oneStarMin && ind === 0) {
					item.classList.add("full");
				} else if (ind === starItem.stars - 1) {
					if (starItem.full) {
						item.classList.add("full");
					} else if (starItem.half) {
						item.classList.add("half");
					}
				} else {
					item.classList.add("full");
				}
			}
		});
	}

	_getItemFromEvent(event: MouseEvent) {
		const bcr = this._dom.starsInner.getBoundingClientRect();
		const xAxis = event.clientX - bcr.left;

		return this._starsIntervals.filter(item => xAxis >= item.from && xAxis <= item.to)[0];
	}

	_getStarsItem(percent = 0) {
		for (let ind = this._starsIntervals.length - 1; ind >= 0; ind--) {
			const item = this._starsIntervals[ind];

			if (percent >= item.percent) {
				return item;
			}
		}

		return this._starsIntervals[0];
	}

	_getStarsIntervals() {
		const middleWidth = this._opts.starWidth / 2;
		const itemPerPercent = 100 / (this._opts.starCount * 2);
		const intervals = [];
		let offsetX = 0;
		let percent = itemPerPercent;

		for (let ind = 1; ind <= this._opts.starCount; ind++) {
			intervals.push({
				stars: ind,
				half: true,
				full: false,
				from: offsetX,
				to: offsetX + middleWidth,
				percent,
			});
			offsetX += middleWidth;
			percent += itemPerPercent;
			intervals.push({
				stars: ind,
				half: false,
				full: true,
				from: offsetX,
				to: offsetX + middleWidth,
				percent,
			});
			offsetX += middleWidth;

			if (ind !== this._opts.starCount) {
				intervals.push({
					stars: ind,
					half: false,
					full: true,
					from: offsetX,
					to: offsetX + this._opts.starOffset,
					percent,
				});
				offsetX += this._opts.starOffset;
			}
			percent += itemPerPercent;
		}

		// upravy pro min 1. hvezda plus a 20% min
		intervals[0].half = false;
		intervals[0].percent = 20;

		return intervals;
	}
}
